import { ChevronDown } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MenuItem, menuItems } from "./MenuItems";
import { usePermissions } from "../hooks/usePermissions";

interface SidebarProps {
  isOpen: boolean;
  isCollapsed: boolean;
  onToggle: () => void;
  onClose?: () => void;
  gradientEnabled?: boolean;
}

export default function Sidebar({ isCollapsed, onClose }: SidebarProps) {
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const [hoveredMenu, setHoveredMenu] = useState<string | null>(null);
  const [hoveredElement, setHoveredElement] = useState<HTMLElement | null>(
    null
  );
  const hoverTimeoutRef = useRef<any | null>(null);
  const [isDesktop, setIsDesktop] = useState(false);

  const { can, canAny, isReady } = usePermissions();

  /**
   * Filter menu items + their children by permission. Top-level items with no
   * permissible children disappear entirely. While `isReady === false` we
   * render everything (the redirect-after-login moment is brief) — once the
   * user-info fetch resolves we filter for real.
   */
  const visibleMenuItems = useMemo<MenuItem[]>(() => {
    if (!isReady) return menuItems;
    const itemAllowed = (item: MenuItem): boolean => {
      if (item.permission && !can(item.permission)) return false;
      if (item.anyOf && !canAny(item.anyOf)) return false;
      return true;
    };
    return menuItems
      .map((item) => {
        if (item.children) {
          const filteredChildren = item.children.filter(itemAllowed);
          if (filteredChildren.length === 0) return null;
          return { ...item, children: filteredChildren };
        }
        return itemAllowed(item) ? item : null;
      })
      .filter((x): x is MenuItem => x !== null);
  }, [isReady, can, canAny]);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const toggleMenu = (title: string) => {
    if (!isCollapsed) {
      setExpandedMenus((prev) => (prev.includes(title) ? [] : [title]));
    }
  };

  const isActive = (path: string) => location.pathname === path;
  const isParentActive = (item: MenuItem) => {
    if (location.pathname.startsWith(item.path)) return true;
    return item.children?.some((child) => isActive(child.path));
  };

  const handleMouseEnter = (
    e: React.MouseEvent<HTMLElement>,
    itemTitle: string
  ) => {
    if (isCollapsed && isDesktop) {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
      setHoveredMenu(itemTitle);
      setHoveredElement(e.currentTarget);
    }
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLElement>) => {
    if (isCollapsed && isDesktop) {
      const relatedTarget = e.relatedTarget as HTMLElement;
      if (
        !relatedTarget?.closest(".dropdown-menu") &&
        !relatedTarget?.closest(".menu-item")
      ) {
        hoverTimeoutRef.current = setTimeout(() => {
          setHoveredMenu(null);
          setHoveredElement(null);
        }, 150);
      }
    }
  };

  const handleDropdownMouseEnter = (itemTitle: string) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setHoveredMenu(itemTitle);
  };

  const handleDropdownMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredMenu(null);
      setHoveredElement(null);
    }, 150);
  };

  const getDropdownStyle = (itemTitle: string, isTooltip = false) => {
    if (hoveredMenu !== itemTitle) return {};
    if (!hoveredElement) return {};

    const rect = hoveredElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownHeight = isTooltip ? 50 : 320;

    let top = rect.top;
    let left = rect.right + 8;

    // For tooltips, center vertically
    if (isTooltip) {
      top = rect.top + rect.height / 2 - 25;
    }

    // Check if dropdown would go below viewport
    if (top + dropdownHeight > viewportHeight) {
      if (isTooltip) {
        top = rect.top + rect.height / 2 - 25;
      } else {
        top = viewportHeight - dropdownHeight - 20;
      }
    }

    // Check if dropdown would go above viewport
    if (top < 20) {
      top = 20;
    }

    return {
      position: "fixed" as const,
      left: `${left}px`,
      top: `${top}px`,
      zIndex: 9999,
    };
  };

  return (
    <div className="flex flex-col h-full min-h-0 relative">
      {/* Scrollable Menu */}
      <nav
        className="flex-1 overflow-y-auto p-4 min-h-0 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent hover:scrollbar-thumb-gray-400"
        style={{
          scrollbarWidth: "thin",
          scrollbarColor: "#d1d5db transparent",
        }}
      >
        <ul className="space-y-1">
          {visibleMenuItems.map((item) => (
            <li
              key={item.path}
              className="relative menu-item"
              onMouseEnter={(e) => handleMouseEnter(e, item.title)}
              onMouseLeave={handleMouseLeave}
            >
              {item.children ? (
                <>
                  <div
                    onClick={() => toggleMenu(item.title)}
                    className={`w-full flex items-center justify-between px-2 lg:px-3 py-2.5 rounded-md transition-all duration-200 cursor-pointer group ${
                      isParentActive(item)
                        ? "bg-blue-50 text-blue-600"
                        : "text-gray-700 hover:bg-blue-50 hover:text-blue-600"
                    } ${isCollapsed ? "lg:justify-center" : ""}`}
                  >
                    <div
                      className={`flex items-center ${
                        isCollapsed ? "lg:justify-center" : "gap-3"
                      }`}
                    >
                      <span
                        className={`transition-colors duration-200 ${
                          isParentActive(item)
                            ? "text-blue-600"
                            : `${item.iconColor || "text-gray-600"} group-hover:text-blue-500`
                        }`}
                      >
                        {item.icon}
                      </span>
                      {!isCollapsed && (
                        <span className="text-[13px] font-medium transition-colors duration-200">
                          {item.title}
                        </span>
                      )}
                    </div>
                    {!isCollapsed && (
                      <ChevronDown
                        className={`w-4 h-4 transition-all duration-200 ${
                          expandedMenus.includes(item.title) ? "rotate-180" : ""
                        } ${
                          isParentActive(item)
                            ? "text-blue-600"
                            : "text-gray-700 group-hover:text-blue-500"
                        }`}
                      />
                    )}
                  </div>

                  {/* Expanded submenu for non-collapsed sidebar */}
                  {!isCollapsed && expandedMenus.includes(item.title) && (
                    <ul className="ml-4 mt-1 space-y-1">
                      {item.children.map((child) => (
                        <li key={child.path}>
                          <Link
                            to={child.path}
                            onClick={() => {
                              if (!isDesktop && onClose) {
                                onClose();
                              }
                            }}
                            className={`flex items-center justify-between px-3 py-2 rounded-lg transition-all duration-200 group relative ${
                              isActive(child.path)
                                ? "bg-blue-100 text-blue-600 font-medium shadow-sm border-l-3 border-blue-500"
                                : "text-gray-600 hover:bg-blue-50 hover:text-blue-600"
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <span
                                className={`transition-colors duration-200 ${
                                  isActive(child.path)
                                    ? "text-blue-600"
                                    : `${child.iconColor || "text-gray-500"} group-hover:text-blue-500`
                                }`}
                              >
                                {child.icon}
                              </span>
                              <span className="text-[13px]">{child.title}</span>
                            </div>
                            {child.badge && (
                              <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                                {child.badge}
                              </span>
                            )}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              ) : (
                <Link
                  to={item.path}
                  onClick={() => {
                    if (!isDesktop && onClose) {
                      onClose();
                    }
                  }}
                  className={`flex items-center px-3 py-2.5 rounded-md transition-all duration-200 group relative ${
                    isActive(item.path)
                      ? "bg-blue-50 text-blue-600 font-medium"
                      : "text-gray-700 hover:bg-blue-50 hover:text-blue-600"
                  } ${isCollapsed ? "lg:justify-center" : "gap-3"}`}
                >
                  <span
                    className={`transition-colors duration-200 ${
                      isActive(item.path)
                        ? "text-blue-600"
                        : `${item.iconColor || "text-gray-600"} group-hover:text-blue-500`
                    }`}
                  >
                    {item.icon}
                  </span>
                  {!isCollapsed && (
                    <span className="text-[13px] font-medium transition-colors duration-200">
                      {item.title}
                    </span>
                  )}
                </Link>
              )}
            </li>
          ))}
        </ul>
      </nav>

      {/* Portal-style dropdowns and tooltips that render at the end */}
      {isCollapsed && hoveredMenu && isDesktop && hoveredElement && (
        <>
          {/* Find the menu item to show dropdown/tooltip for */}
          {visibleMenuItems.find((item) => item.title === hoveredMenu)?.children ? (
            // Dropdown for menu items with children
            <div
              className="dropdown-menu bg-white rounded-xl shadow-2xl border border-gray-200 py-2 w-56"
              style={getDropdownStyle(hoveredMenu, false)}
              onMouseEnter={() => handleDropdownMouseEnter(hoveredMenu)}
              onMouseLeave={handleDropdownMouseLeave}
            >
              <div className="px-3 py-2 border-b border-gray-100 mb-1">
                <p className="text-sm font-semibold text-gray-900">
                  {hoveredMenu}
                </p>
              </div>
              {visibleMenuItems
                .find((item) => item.title === hoveredMenu)
                ?.children?.map((child) => (
                  <Link
                    key={child.path}
                    to={child.path}
                    className={`flex items-center justify-between px-3 py-2 mx-2 rounded-lg transition-all duration-200 group ${
                      isActive(child.path)
                        ? "bg-blue-100 text-blue-600 font-medium shadow-sm"
                        : "text-gray-600 hover:bg-blue-50 hover:text-blue-600"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className={`transition-colors duration-200 ${
                          isActive(child.path)
                            ? "text-blue-600"
                            : `${child.iconColor || "text-gray-500"} group-hover:text-blue-500`
                        }`}
                      >
                        {child.icon}
                      </span>
                      <span className="text-[13px]">{child.title}</span>
                    </div>
                    {child.badge && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                        {child.badge}
                      </span>
                    )}
                  </Link>
                ))}
            </div>
          ) : (
            // Tooltip for single menu items and logout
            <div
              className="dropdown-menu px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg whitespace-nowrap"
              style={getDropdownStyle(hoveredMenu, true)}
              onMouseEnter={() => handleDropdownMouseEnter(hoveredMenu)}
              onMouseLeave={handleDropdownMouseLeave}
            >
              {hoveredMenu}
            </div>
          )}
        </>
      )}
    </div>
  );
}
